import type { BackfillComputed, BackfillComputedInfo } from '../types';
import type { ServicesApiBackfillPriority, BackfillState } from '@/services/api/services-api';

export const BACKFILL_TYPES = {
  Segment: {
    icon: 'segment',
    label: 'Build Segment',
    status: 'Segment build in progress…',
    objectType: 'segment',
    detailRoute: '/workspaces/:workspaceId/journeys/segments/:segmentId',
  },
  DeleteSegment: {
    icon: 'segment',
    label: 'Delete Segment',
    status: 'Segment delete in progress…',
    objectType: 'segment',
    detailRoute: '/workspaces/:workspaceId/journeys/segments/:segmentId',
  },
  ClearStaticSegment: {
    icon: 'segment-manual',
    label: 'Clear Manual Segment',
    status: 'Segment clear in progress…',
    objectType: 'segment',
    detailRoute: '/workspaces/:workspaceId/journeys/segments/:segmentId',
  },
  CollectionImport: {
    icon: 'import',
    label: 'Import Collection',
    status: 'Collection import in progress…',
    objectType: 'collection',
    detailRoute: '/workspaces/:workspaceId/journeys/collections/:collectionId',
  },
  ConditionSchedule: {
    icon: 'segment',
    label: 'Schedule Condition',
    status: 'Segment optimization scheduling…',
    objectType: 'segment',
    detailRoute: '/workspaces/:workspaceId/journeys/segments/:segmentId',
  },
  CampaignStop: {
    icon: 'campaigns',
    label: 'Stop Campaign',
    status: 'Stopping Campaign in progress…',
    objectType: 'campaign',
    detailRoute: '/workspaces/:workspaceId/journeys/campaigns/:campaignId',
  },
  CampaignArchive: {
    icon: 'campaigns',
    label: 'Archive Campaign',
    status: 'Archiving Campaign in progress…',
    objectType: 'campaign',
    detailRoute: '/workspaces/:workspaceId/journeys/campaigns/:campaignId',
  },
  CampaignBackfill: {
    icon: 'campaigns',
    label: 'Backfill Campaign',
    status: 'Including existing matches…',
    objectType: 'campaign',
    detailRoute: '/workspaces/:workspaceId/journeys/campaigns/:campaignId',
  },
  CampaignDelete: {
    icon: 'campaigns',
    label: 'Delete Campaign',
    status: 'Campaign delete in progress…',
    objectType: 'campaign',
    detailRoute: '/workspaces/:workspaceId/journeys/campaigns/:campaignId',
  },
  ActionBackfill: {
    icon: 'campaigns',
    label: 'Update Action',
    description: 'Updated time window or delay',
    status: 'Moving people out of the delay…',
    objectType: 'campaign',
    detailRoute: '/workspaces/:workspaceId/journeys/campaigns/:campaignId',
  },
  ActionDelete: {
    icon: 'campaigns',
    label: 'Delete Action',
    description: 'Deleted an action',
    status: 'Action delete in progress…',
    objectType: 'campaign',
    detailRoute: '/workspaces/:workspaceId/journeys/campaigns/:campaignId',
  },
  NewsletterSend: {
    icon: 'newsletter',
    label: 'Prepare Newsletter',
    status: 'Queuing deliveries…',
    objectType: 'newsletter',
    detailRoute: '/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId',
  },
  DeliveriesManage: {
    icon: 'email',
    label: 'Manage Deliveries',
    description: 'Processed bulk delivery sends or deletes',
    status: 'Processing bulk delivery actions…',
  },
  Export: {
    icon: 'export',
    label: 'Export Data',
    description: 'Exported people, metrics, or deliveries',
    objectType: 'export',
    status: 'Building export data…',
  },
  TriggeredBroadcast: {
    icon: 'api-triggered-broadcast',
    label: 'API Triggered Broadcast',
    status: 'Processing broadcast…',
    objectType: 'broadcast-trigger',
    detailRoute: '/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/overview/broadcasts/:triggerId',
  },
  ProcessPerUserDataForBroadcast: {
    icon: 'api-triggered-broadcast',
    label: 'API Triggered Broadcast Including Custom Data',
    status: 'Processing broadcast…',
    objectType: 'broadcast-trigger',
    detailRoute: '/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/overview/broadcasts/:triggerId',
  },
  ImportPreprocess: {
    icon: 'import',
    label: 'Import Preprocess',
    status: 'Import preprocessing in progress…',
    objectType: 'import',
    description: 'Uploaded import file',
  },
  ImportProcess: {
    icon: 'import',
    label: 'Import Process',
    status: 'Import in progress…',
    objectType: 'import',
    description: 'Imported CSV',
  },
  ChangeRestartSettings: {
    icon: 'campaigns',
    label: 'Change Campaign Frequency',
    status: 'Changing Campaign frequency settings…',
    objectType: 'campaign',
    detailRoute: '/workspaces/:workspaceId/journeys/campaigns/:campaignId',
    description: "Changed Campaign's frequency",
  },
  SQLImport: {
    icon: 'database',
    label: 'SQL Sync',
    status: 'SQL Sync in progress…',
    description: 'Imported people',
    objectType: 'sql-import',
    detailRoute: '/workspaces/:workspaceId/journeys/sql-imports/:importId',
  },
  'Migration to enable only id to identify people': {
    icon: 'workspaces',
    label: 'Workspace migration (ID only)',
    status: 'Migration to enable only id to identify people…',
    description: 'Migration to enable only id to identify people',
  },
  'Migration to enable email or id to identify people': {
    icon: 'workspaces',
    label: 'Workspace migration (Email or ID)',
    status: 'Migration to enable email or id to identify people…',
    description: 'Migration to enable email or id to identify people',
  },
  'Identifier type migration': {
    icon: 'workspaces',
    label: 'Workspace migration (Identifier type)',
    status: 'Identifier type migration…',
    description: 'Identifier type migration',
  },
  CancelQueuedInApps: {
    icon: 'in-app',
    label: 'Cancel Queued In-App Messages',
    status: 'Canceling queued in-app messages…',
    description: 'Canceled queued in-app messages',
  },
  Alias: {
    icon: 'people',
    label: 'Alias',
    status: 'Processing alias…',
    description: 'Alias task',
  },
} as const satisfies Record<string, BackfillComputedInfo>;

export type BackfillType = keyof typeof BACKFILL_TYPES;

export function getTaskTypeInfo(type: BackfillType): BackfillComputedInfo {
  return (
    BACKFILL_TYPES[type] ??
    ({
      icon: '',
      label: type,
      description: '',
      status: 'Processing…',
    } as const)
  );
}

export function generateBackfillRoute(backfill: BackfillComputed, workspaceId: number): string | null {
  const { info, object_id, object_version, related_object_id } = backfill;

  if (object_id == null || info.detailRoute == null) {
    return null;
  }

  // When the backend exposes a related object id (e.g. the parent of a temp
  // preview segment, whose own detail route 404s), navigate to that instead of
  // the literal object_id.
  const linkObjectId = related_object_id ?? object_id;

  // Broadcast-trigger detail routes deep-link to a specific trigger, whose id is
  // the composite `<broadcastId>-<triggerVersion>` (e.g. 1000175-215). When the
  // version is missing we fall back to the broadcasts (triggers) tab rather than
  // emitting a dangling `<broadcastId>-` id.
  const triggerId = object_version != null ? `${linkObjectId}-${object_version}` : '';

  return info.detailRoute
    .replace(':workspaceId', String(workspaceId))
    .replace(':segmentId', String(linkObjectId))
    .replace(':campaignId', String(linkObjectId))
    .replace(':newsletterId', String(linkObjectId))
    .replace(':broadcastId', String(linkObjectId))
    .replace(':collectionId', String(linkObjectId))
    .replace(':importId', String(linkObjectId))
    .replace(':triggerId', triggerId);
}

export function getPriorityLabel(priority: string): string {
  const priorityMap: Record<string, string> = {
    Priority: 'High',
    Default: 'Medium',
    Background: 'Low',
    Import: 'Medium',
    SendNewsletter: 'Medium',
    ProfileExport: 'Medium',
    DeliveriesExport: 'Medium',
    SQL_Import: 'Medium',
    RunAlias: 'Medium',
  };

  return priorityMap[priority] ?? 'Medium';
}

export function getPriorityQueueLabel(priority: string): string {
  const priorityMap: Record<string, string> = {
    Priority: 'High priority queue',
    Default: 'Medium priority queue',
    Background: 'Low priority queue',
    Import: 'Import queue',
    SendNewsletter: 'Newsletter sends queue',
    ProfileExport: 'Profile exports queue',
    DeliveriesExport: 'Delivery exports queue',
    SQL_Import: 'SQL imports queue',
    RunAlias: 'Alias processing queue',
  };

  return priorityMap[priority] ?? priority;
}

export function getPriorityQueueShortLabel(priority: string): string {
  const priorityMap: Record<string, string> = {
    Priority: 'High Priority',
    Default: 'Medium Priority',
    Import: 'Imports',
    SendNewsletter: 'Newsletter Sends',
    ProfileExport: 'Profile Exports',
    DeliveriesExport: 'Delivery Exports',
    SQL_Import: 'SQL Imports',
  };

  return priorityMap[priority] ?? getPriorityQueueLabel(priority);
}

export const PRIORITY_QUEUE_ORDER = [
  'Priority',
  'Default',
  'SendNewsletter',
  'Import',
  'SQL_Import',
  'ProfileExport',
  'DeliveriesExport',
] as const;

const QUEUE_EDGE_MOVABLE_STATES: BackfillState[] = ['Queued', 'InProgress', 'Retrying'];

export function isQueueEdgeMovableState(state: BackfillState): boolean {
  return QUEUE_EDGE_MOVABLE_STATES.includes(state);
}

export function getQueueMoveDestinationLabel(position: 'first' | 'last'): string {
  return position === 'first' ? 'next' : 'last';
}

export function getQueueMoveActionLabel(state: BackfillState, position: 'first' | 'last'): string {
  const destination = getQueueMoveDestinationLabel(position);

  if (state === 'InProgress') return `Interrupt and move to ${destination} in queue`;
  if (state === 'Retrying') return `Move retry to ${destination} in queue`;

  return `Move to ${destination} in queue`;
}

export function getDeprioritizeTooltip(state: BackfillState, mode: 'first' | 'last'): string {
  const destination = getQueueMoveDestinationLabel(mode);
  const deprioritizeNextMessage = 'Move this queued task to next in queue.';
  const deprioritizeLastMessage = 'Move this queued task to last in queue.';
  const deprioritizeMessage = mode === 'first' ? deprioritizeNextMessage : deprioritizeLastMessage;

  const tooltipMap: Record<BackfillState, string> = {
    Success: 'This task cannot be moved because it has already completed successfully.',
    Failure: 'This task cannot be moved because it has already failed.',
    Canceled: 'This task cannot be moved because it has been canceled.',
    InProgress: `Interrupt the current attempt and move this task to ${destination} in queue.`,
    Queued: deprioritizeMessage,
    Retrying: `Move this retrying task to ${destination} in queue.`,
  };

  return tooltipMap[state] ?? deprioritizeMessage;
}

export function getCancelTooltip(state: BackfillState): string {
  const cancelMessage = 'Cancel this task and clean up any partial progress.';

  const tooltipMap: Record<BackfillState, string> = {
    Success: 'This task cannot be canceled because it has already completed successfully.',
    Failure: 'This task cannot be canceled because it has already failed.',
    Canceled: 'This task has already been canceled.',
    InProgress: cancelMessage,
    Queued: cancelMessage,
    Retrying: cancelMessage,
  };

  return tooltipMap[state] ?? cancelMessage;
}

function getDefaultPriorityForType(type: string) {
  const typeToPriorityMap: Record<string, ServicesApiBackfillPriority> = {
    // Priority (High)
    CampaignBackfill: 'Priority',
    CampaignStop: 'Priority',
    CampaignArchive: 'Priority',
    ActionBackfill: 'Priority',
    ChangeRestartSettings: 'Priority',
    TriggeredBroadcast: 'Priority',
    DeliveriesManage: 'Priority',
    Temporary: 'Priority',

    // Default (Medium)
    Segment: 'Default',
    ExternalSearchIndex: 'Default',

    // Background (Low)
    CampaignDelete: 'Background',
    ActionDelete: 'Background',
    DeleteSegment: 'Background',
    ConditionSchedule: 'Background',
    CancelQueuedInApps: 'Background',
    CleanAttrIndexes: 'Background',
    DeleteAudience: 'Background',
    DeleteCustomers: 'Background',
    CancelQueuedTransactional: 'Background',
    DeleteObjectType: 'Background',
    DeleteObjects: 'Background',
    RedirectTemplates: 'Background',
    InAppBroadcastHandler: 'Background',
    DeleteAttribute: 'Background',
    DeleteObjectAttribute: 'Background',
    DeleteObjectRelAttribute: 'Background',

    // Import
    ImportPreprocess: 'Import',
    ImportProcess: 'Import',
    ClearStaticSegment: 'Import',
    CollectionImport: 'Import',
    ProcessPerUserDataForBroadcast: 'Import',

    // SQL_Import
    SQLImport: 'SQL_Import',

    // SendNewsletter
    NewsletterSend: 'SendNewsletter',

    // Export types use either ProfileExport or DeliveriesExport
    // depending on export subtype - defaults to DeliveriesExport
    Export: 'DeliveriesExport',
  };

  return typeToPriorityMap[type] ?? 'Default';
}
/**
 * Determines if a task type should be hidden from the filter dropdown.
 */
export function isInvisibleLabel(label: BackfillType): boolean {
  // Migration types contain spaces, e.g. 'Migration to enable email or id to identify people'
  if (label.includes(' ')) {
    return true;
  }

  if (label === 'ActionDelete') {
    return false;
  }

  if (label === 'Alias') {
    return true;
  }

  return getDefaultPriorityForType(label) === 'Background';
}
